import { useRouter } from 'next/router';
import {
  useEffect,
  useContext,
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useState
} from 'react';

type FileData = {
  file: File | undefined;
  imagePreviewUrl: string | string[];
};

type Context = {
  fileData: FileData;
  setFileData: Dispatch<SetStateAction<FileData>>;
  imageUploadPromise: Promise<any>;
  setImageUploadPromise: Dispatch<SetStateAction<Promise<any>>>;
};

const TransformXContext = createContext<Context | undefined>(undefined);

export const TransformXContextProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const router = useRouter();
  const [fileData, setFileData] = useState<FileData>({
    file: undefined,
    imagePreviewUrl: undefined
  });
  const [imageUploadPromise, setImageUploadPromise] = useState<Promise<any> | undefined | null>();

  useEffect(() => {
    if (router.query.imageUrl) {
      setFileData(p => ({ ...p, imagePreviewUrl: router.query.imageUrl }));
    }
  }, [router.query.imageUrl]);

  useEffect(() => {
    function handleUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = 'We are uplaoding your image. Are you sure you want to leave?';
    }
    if (imageUploadPromise && imageUploadPromise !== null) {
      imageUploadPromise.finally(() => {
        setImageUploadPromise(null);
      });
      window.addEventListener('beforeunload', handleUnload);
      return () => {
        window.removeEventListener('beforeunload', handleUnload);
      };
    }
  }, [imageUploadPromise]);

  return (
    <TransformXContext.Provider
      value={{ fileData, setFileData, imageUploadPromise, setImageUploadPromise }}
    >
      {children}
    </TransformXContext.Provider>
  );
};

export const useTransformXContext = () => {
  const ctx = useContext(TransformXContext);
  if (ctx === undefined) {
    throw new Error('useTransformXContext must be used below a TransformXContextProvider');
  }
  return ctx;
};
